import { pushGaEvent } from './pushGaEvent';

export interface TrackingParamResolved {
  key?: string | null;
  value?: string | null;
}

export interface EventTrackingResolved {
  internalLabel?: string | null;
  trackingEvent?: TrackingParamResolved[];
}

// Placeholder tokens an Editor can reference inside a Tracking Param's own
// `value` (e.g. "{label}\n{phone}") — substituted with values the website
// computes at click time (an item's own label, the resolved phone, the
// current page URL). The CMS only ever stores the literal token text; which
// values exist and what they resolve to is entirely up to the caller.
export type TrackingSubstitutions = Partial<
  Record<'label' | 'phone' | 'pageUrl', string>
>;

const TOKEN_PATTERN = /\{(label|phone|pageUrl)\}/g;

function substitute(value: string, tokens: TrackingSubstitutions): string {
  return value.replace(
    TOKEN_PATTERN,
    (match, token: keyof TrackingSubstitutions) => tokens[token] ?? match,
  );
}

// Pushes one `pushGaEvent` call per CMS-configured Event Tracking entry.
// Each entry's `trackingEvent` key-value pairs become the pushed event's
// fields. `tokens` are substituted into every string value before pushing,
// so a single entry can carry a dynamic value (e.g. `{label}`) without the
// Editor having to retype it per location/item.
export function pushCmsEventTracking(
  entries: EventTrackingResolved[] | undefined,
  tokens: TrackingSubstitutions = {},
): void {
  for (const entry of entries ?? []) {
    const fields: Record<string, string> = {};
    for (const param of entry.trackingEvent ?? []) {
      if (!param.key) {
        continue;
      }
      fields[param.key] = substitute(param.value ?? '', tokens);
    }

    pushGaEvent(fields);
  }
}
